/* eslint-disable object-curly-newline,implicit-arrow-linebreak */
import { useCallback, useMemo } from 'react'
import { Box, Chip as MuiChip, useTheme } from '@mui/material'
import { getThemeColor, PlatformEvent } from 'lib/util'
import { lighten } from 'lib/util/styles'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import DecoratedText from 'ui/Element/Text/Decorated'

const styles = (theme, { disabled, shape, color, backgroundColor, size, canHover }) => {
  const defaultBackgroundColor = (disabled && theme.palette.gray[950])
    || (backgroundColor && getThemeColor(theme, backgroundColor))
    || theme.palette.gray[960]
  const hasNoBackground = !backgroundColor || backgroundColor === 'transparent'

  return {
    root: {
      fontFamily: 'unset', // Will force Roboto otherwise
      height: 'unset',
      minHeight: 'unset',

      maxWidth: '100%',
      minWidth: 0,

      '& .MuiChip-icon': {
        marginLeft: 0,
        marginRight: '0.25rem',
        flexShrink: 0,
      },

      '& .MuiChip-deleteIcon': {
        marginLeft: '0.25rem',
        marginRight: 0,
      },

      '&:hover': {
        backgroundColor: canHover
          ? (disabled && theme.palette.gray[950])
            // Instead of darkening backgroundColor, we lighten color
            || (color && lighten(getThemeColor(theme, color), 0.8))
            || theme.palette.gray[950]
          : defaultBackgroundColor,
      },

      backgroundColor: defaultBackgroundColor,

      ...(!size || size === 'medium') && !hasNoBackground && {
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        paddingLeft: '0.75rem',
        paddingRight: '0.75rem',
      },

      ...size === 'small' && !hasNoBackground && {
        paddingTop: '0.375rem',
        paddingBottom: '0.375rem',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
      },

      ...size === 'tiny' && !hasNoBackground && {
        paddingTop: '0.125rem',
        paddingBottom: '0.125rem',
        paddingLeft: '0.3125rem',
        paddingRight: '0.3125rem',
      },

      ...shape === 'rounded' && {
        borderRadius: '0.25rem',
      },
      ...shape === 'circle' && {
        borderRadius: Number.MAX_SAFE_INTEGER,
      },
    },
    label: {
      color: getThemeColor(theme, color),
    },
    text: {
      whiteSpace: 'nowrap !important',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    labelSmall: {
      padding: 0,
    },
    labelMedium: {
      padding: 0,
    },
    chevron: {
      // Keep the chevron a fixed size regardless of the chip's width/height
      flexShrink: 0,
    },
  }
}

const iconSizeMapping = {
  tiny: 10,
  small: 12,
  medium: 16,
}

// Not needed, height is already correct, but maybe we want to set it explicitly
// const heightMapping = {
//   tiny: '1.25rem',
//   small: '1.5rem',
//   medium: '2.375rem',
// }

export const fontSizeMapping = {
  tiny: '12/medium',
  small: '12/medium',
  medium: '14/bold',
}

/**
 * Chip UI Element.
 *
 * Simple element used to display dynamic text with an `icon`, which can also be given
 * `variant`, `color` and `outline` properties.
 *
 * TODO: Implement variants ('filled', 'outlined') once we need it
 *
 * @param {Object} forwardedRef                         a reference to the root element
 * @param {string|Object} [icon]                        icon to display for the chip
 * @param {string} value                                text to display for the chip
 * @param {string} [color]                              color for the chip
 * @param {backgroundColor} [backgroundColor]           background color for the chip
 * @param {boolean} [chevron=false]                     whether to display a trailing chevron
 * @param {Object} props                                additional props
 * @param {'rounded'|'circle'} [props.shape='circle']   whether the chip should be rounded
 * @param {ChipSize} [props.size='medium']              size of the chip
 * @param {string} [props.maxWidth='100%']              maximum width of the chip
 * @param {boolean} [props.disabled]                    whether the chip is disabled
 * @param {string} [props.textVariant]                  variant for the text to override default
 * @param {Object} [props.decorators]                   decorators for the text
 * @param {number} [props.iconSize]                     size override for the leading icon
 * @param {string} [props.iconColor]                    color override for the leading icon
 * @param {number} [props.chevronSize]                  size override for the trailing chevron
 * @param {string} [props.chevronColor]                 color override for the trailing chevron
 * @param {string} [props.chevronIcon]                  icon to use for the chevron
 * @param {Object} [props.events]                       whether the chip should be rounded
 * @param {Function} [props.events.onClick]             onClick handler
 * @param {Function} [props.events.onLongClick]         onLongClick handler
 * @param {Function} [props.events.onClose]             onClose handler
 * @returns {JSX.Element}                               the new Chip element
 */
// eslint-disable-next-line max-len
const Chip = ({
  forwardedRef,
  icon,
  value,
  color = 'black.main',
  backgroundColor = 'gray.960',
  chevron = false,
  chevronIcon = 'chevron-down',
  ...props
}) => {
  const theme = useTheme()

  const { shape = 'circle', size = 'medium', ...moreProps } = props
  const {
    disabled, textVariant, events, decorators, variant,
    iconSize = null, iconColor = null, chevronSize = null, chevronColor = null,
  } = moreProps

  const classes = useStyles(
    styles,
    { disabled, size, shape, color, backgroundColor, canHover: !!events?.onClick },
  )()

  const handleClick = useCallback((event) => {
    !disabled && events?.onClick?.(new PlatformEvent(event))
  }, [events?.onClick, disabled])

  const handleClose = useCallback((event) => {
    !disabled && events?.onClose?.(new PlatformEvent(event))
  }, [events?.onClose, disabled])

  const targetColor = useMemo(
    () => (disabled ? theme.palette.gray[600] : getThemeColor(theme, color)),
    [color, disabled],
  )

  const iconComponent = useMemo(() => (!icon ? null : (
    <Icon
      source={icon}
      color={iconColor || targetColor}
      // 100% seems too big, adjust accordingly if we get a design for it
      size={iconSize || iconSizeMapping[size]}
    />
  )), [icon, iconColor, iconSize, targetColor, size])

  const deleteComponent = useMemo(() => (!events?.onClose ? null : (
    <Box onClick={handleClose}>
      <Icon
        name={'close'}
        color={targetColor}
        // 100% seems too big, adjust accordingly if we get a design for it
        size={iconSizeMapping[size]}
      />
    </Box>
  )), [events])

  return (
    <MuiChip
      ref={forwardedRef}
      // variant={variant}
      classes={classes}
      clickable={!!events?.onClick}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
          <DecoratedText
            color={targetColor}
            value={value}
            variant={textVariant || fontSizeMapping[size] }
            className={classes.text}
            decorators={decorators}
          />
          {chevron && (
            <Icon
              name={chevronIcon}
              color={chevronColor || targetColor}
              size={chevronSize || iconSizeMapping[size]}
              className={classes.chevron}
            />
          )}
        </Box>
      }
      {...icon && { icon: iconComponent }}
      {...events?.onClose && { deleteIcon: deleteComponent }}
      onDelete={events?.onClose ? handleClose : undefined}
      onClick={events?.onClick ? handleClick : undefined}
    />
  )
}

export default useMemoRef(Chip, props => [
  props.value,
  props.icon,
  props.color,
  props.variant,
  props.events,
  props.disabled,
  props.chevron,
  props.chevronIcon,
])
